import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import moment from "moment";
import erpInventoryServices from "app/services/erp-inventory-services";

const COVER_COLOR = { critico: "error", alerta: "warning", ok: "success" };

// Ficha de producto.
//
// Lo que aporta sobre la fila del listado es el HISTORIAL: los numeros de
// rotacion y cobertura no se pueden auditar desde la tabla. Ver "7.457 dias" no
// dice nada hasta que uno ve que vendio 12 unidades en 90 dias, de a una.
const ProductDetailDrawer = ({ code, open, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        if (!code) return;
        try {
            setLoading(true);
            setData(await erpInventoryServices.getDetail(code));
        } catch (_error) {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [code]);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    const demand = data?.demand;

    return (
        <Drawer anchor="right" open={open} onClose={onClose}
                PaperProps={{ sx: { width: { xs: "100%", md: 560 } } }}>
            <Box sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                    <Box>
                        <Typography variant="h5" sx={{ mb: 0 }}>{data?.name || code}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {code}{data?.barcode ? ` · EAN ${data.barcode}` : " · sin EAN"}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose}><CloseOutlinedIcon /></IconButton>
                </Stack>

                {loading && <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>}

                {!loading && data && (
                    <Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                            <Chip label={`${data.total_stock} ${data.unit || ""} en stock`} />
                            {demand?.abc_class && (
                                <Chip label={`Clase ${demand.abc_class}`}
                                      color={demand.abc_class === "A" ? "primary" : "default"} variant="outlined" />
                            )}
                            {data.days_of_cover !== null && data.days_of_cover !== undefined && (
                                <Chip label={`${data.days_of_cover} días de cobertura`}
                                      color={COVER_COLOR[data.cover_level] || "default"} />
                            )}
                        </Stack>

                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Stock por bodega</Typography>
                        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                            <Stack spacing={0.75}>
                                {data.warehouses?.map((w) => (
                                    <Stack key={w.centre_id || w.centre_name} direction="row"
                                           justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2">{w.centre_name || "Sin bodega"}</Typography>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            {w.bin_code && <Chip size="small" variant="outlined" label={w.bin_code} />}
                                            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 50, textAlign: "right" }}>
                                                {w.stock}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                ))}
                            </Stack>
                        </Paper>

                        {demand && (
                            <>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Rotación en {demand.window_days} días
                                </Typography>
                                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                                    <Box>
                                        <Typography variant="h4" sx={{ mb: 0 }}>{demand.picks}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            veces que se fue a buscar
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="h4" sx={{ mb: 0 }}>{demand.units}</Typography>
                                        <Typography variant="caption" color="text.secondary">unidades salidas</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="h4" sx={{ mb: 0 }}>
                                            {demand.days_since_movement ?? "—"}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            días desde el último
                                        </Typography>
                                    </Box>
                                </Stack>
                            </>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Últimas salidas</Typography>
                        {data.movements?.length ? (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Fecha</TableCell>
                                        <TableCell align="right">Cantidad</TableCell>
                                        <TableCell>Pedido</TableCell>
                                        <TableCell>Canal</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.movements.map((m, index) => (
                                        <TableRow key={`${m.order_code}-${index}`}>
                                            <TableCell>
                                                <Tooltip title={moment(m.date).format("DD/MM/YYYY HH:mm")}>
                                                    <span>{moment(m.date).format("DD/MM/YY")}</span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell align="right">{m.qty}</TableCell>
                                            <TableCell>{m.order_code}</TableCell>
                                            <TableCell>
                                                <Typography variant="caption" color="text.secondary">
                                                    {m.channel || "—"}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Sin salidas registradas.
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        </Drawer>
    );
};

export default ProductDetailDrawer;
